import { isDebugging } from '@utils/constants';
import { isObject, mergeObjects } from '@utils/objects';

/**
 * Configuration interface for HTTPClient initialization
 */
export interface HTTPClientConfig {
  /** Base URL for all API requests */
  baseURL?: string;
  /** Authorization token for API requests */
  token?: string;
  /** Default headers to include in all requests */
  headers?: Record<string, string>;
  /** Prepare options before request */
  prepareOptions?: (options: RequestOptions) => RequestInit;
}

/**
 * Extended request options interface for HTTP requests
 */
export interface RequestOptions extends RequestInit {
  /** Custom headers for this specific request */
  headers?: Record<string, string>;
}

/**
 * URL query parameters interface
 */
export interface URLParams {
  [key: string]: string | number | boolean;
}

/**
 * Standard API response interface
 * TODO: check all usages and replace with "success: true"
 */
export type APIResponse<T = any> = T | APIResponseError;

export interface APIResponseError {
  success: false;
  error: string;
}

/**
 * Supported HTTP methods
 */
export type HTTPMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD' | 'OPTIONS';

/**
 * Base HTTP client class for making API requests
 * Provides methods for common HTTP operations with built-in error handling and timeout support
 */
export default class HTTPClient {
  protected baseURL: string;
  protected token: string;
  protected headers: Record<string, string>;
  protected lastRequest: Response | null;
  private prepareOptions: (options: RequestOptions) => RequestInit;

  /**
   * Creates a new HTTPClient instance
   *
   * @param config - Configuration options for the HTTP client
   */
  constructor(config: HTTPClientConfig = {}) {
    this.lastRequest = null;

    this.baseURL = config.baseURL ?? '';
    this.token = config.token ?? '';
    this.prepareOptions = config.prepareOptions ?? (options => options);

    this.headers = config.headers ?? {
      'Accept': 'application/json',
      'User-Agent': 'Intranet/1.0',
    };
  }

  /**
   * Sets the authorization token for API requests
   *
   * @param token - The authorization token
   */
  setToken(token: string): void {
    this.token = token;
  }

  /**
   * Sets the base URL for all API requests
   *
   * @param baseURL - The base URL (e.g., 'https://api.example.com')
   */
  setBaseURL(baseURL: string): void {
    this.baseURL = baseURL;
  }

  /**
   * Replace all default headers for all requests
   *
   * @param headers - Object containing header key-value pairs
   */
  setHeaders(headers: Record<string, string>): void {
    this.headers = { ...headers };
  }

  /**
   * Add/replace headers for all requests
   *
   * @param headers - Object containing header key-value pairs
   */
  updateHeaders(headers: Record<string, string>): void {
    this.headers = { ...this.headers, ...headers };
  }

  /**
   * Returns the last HTTP request made by this client
   *
   * @returns The last Response object or null if no request was made
   */
  getLastRequest(): Response | null {
    return this.lastRequest;
  }

  /**
   * Performs a GET request to the specified endpoint
   *
   * @param path - The API endpoint path
   * @param params - URL query parameters
   * @param options - Additional request options
   * @returns Promise resolving to API response with typed data
   */
  async get<T = any>(path: string = '', params: URLParams = {}, options: RequestOptions = {}): Promise<APIResponse<T>> {
    return this.request<T>('GET', path, null, options, params) as Promise<APIResponse<T>>;
  }

  /**
   * Performs a POST request to the specified endpoint
   *
   * @param path - The API endpoint path
   * @param body - Request body data
   * @param options - Additional request options
   * @param params - URL query parameters
   * @returns Promise resolving to API response with typed data
   */
  async post<T = any>(path: string = '', body: any = {}, options: RequestOptions = {}, params: URLParams = {}): Promise<APIResponse<T>> {
    return this.request<T>('POST', path, body, options, params) as Promise<APIResponse<T>>;
  }

  /**
   * Performs a PUT request to the specified endpoint
   *
   * @param path - The API endpoint path
   * @param body - Request body data
   * @param options - Additional request options
   * @param params - URL query parameters
   * @returns Promise resolving to API response with typed data
   */
  async put<T = any>(path: string = '', body: any = {}, options: RequestOptions = {}, params: URLParams = {}): Promise<APIResponse<T>> {
    return this.request<T>('PUT', path, body, options, params) as Promise<APIResponse<T>>;
  }

  /**
   * Performs a DELETE request to the specified endpoint
   *
   * @param path - The API endpoint path
   * @param options - Additional request options
   * @param params - URL query parameters
   * @returns Promise resolving to API response with typed data
   */
  async delete<T = any>(path: string = '', options: RequestOptions = {}, params: URLParams = {}): Promise<APIResponse<T>> {
    return this.request<T>('DELETE', path, null, options, params) as Promise<APIResponse<T>>;
  }

  /**
   * Performs a PATCH request to the specified endpoint
   *
   * @param path - The API endpoint path
   * @param body - Request body data
   * @param options - Additional request options
   * @param params - URL query parameters
   * @returns Promise resolving to API response with typed data
   */
  async patch<T = any>(path: string = '', body: any = {}, options: RequestOptions = {}, params: URLParams = {}): Promise<APIResponse<T>> {
    return this.request<T>('PATCH', path, body, options, params) as Promise<APIResponse<T>>;
  }

  /**
   * Performs a raw HTTP request and returns the Response object directly
   *
   * @param method - HTTP method
   * @param path - The API endpoint path
   * @param body - Request body data
   * @param options - Additional request options
   * @param urlParams - URL query parameters
   * @returns Promise resolving to the raw Response object
   */
  async raw(method: HTTPMethod, path: string, body: any = {}, options: RequestOptions = {}, urlParams: URLParams = {}): Promise<Response> {
    return this.request(method, path, body, options, urlParams, true) as Promise<Response>;
  }

  /**
   * Generates authorization header from stored token
   *
   * @returns Authorization header tuple or null if no token
   */
  protected getAuthorizationHeader(): [string, string] | null {
    return this.token ? ['Authorization', `Bearer ${this.token}`] : null;
  }

  /**
   * Prepares the body for the request
   *
   * @param body - Request body data
   * @returns Prepared body
   */
  protected prepareBody(body: any): any {
    if (body instanceof FormData) {
      return body;
    }

    return isObject(body) ? JSON.stringify(body) : body;
  }

  /**
   * Builds complete URL from path and query parameters
   *
   * @param path - API endpoint path
   * @param params - URL query parameters
   * @returns Complete URL string
   */
  protected buildURL(path: string, params: URLParams = {}): string {
    if (! path) {
      return this.baseURL;
    }

    const baseURL = path.startsWith('http') ? path : `${this.baseURL}/${path}`;
    const url = new URL(baseURL);

    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });

    return url.toString();
  }

  /**
   * Parses and merges request options with default headers and authorization
   *
   * @param options - Request options to parse
   * @returns Processed RequestInit object
   */
  private parseOptions(options: RequestOptions = {}): RequestInit {
    const headers = new Headers();
    const mergedHeaders = mergeObjects(this.headers, options.headers || {});

    Object.entries(mergedHeaders).forEach(([key, value]) => {
      headers.append(key, String(value));
    });

    const authorization = this.getAuthorizationHeader();
    if (authorization) {
      headers.append(authorization[0], authorization[1]);
    }

    const customOptions = this.prepareOptions(options);

    return {
      ...customOptions,
      headers,
    };
  }

  /**
   * Logs debug message if debugging is enabled
   *
   * @param message - Message to log
   */
  protected log(message: string): void {
    if (! isDebugging()) {
      return;
    }

    console.log(`[${this.constructor.name ?? 'HTTP_Client'}] ${message}`);
  }

  /**
   * Logs error message if debugging is enabled
   *
   * @param message - Error message or Error object to log
   */
  protected error(message: string | Error): void {
    if (! isDebugging()) {
      return;
    }

    console.error(`[${this.constructor.name ?? 'HTTP_Client'}] ${message}`);
  }

  protected async request<T = object>(method: HTTPMethod, path: string, body: any, options: RequestOptions, urlParams: URLParams, returnResponse?: false): Promise<APIResponse<T>>;
  protected async request<T = object>(method: HTTPMethod, path: string, body: any, options: RequestOptions, urlParams: URLParams, returnResponse: true): Promise<Response | null>;

  /**
   * Core method for performing HTTP requests with full configuration
   *
   * @param method - HTTP method to use
   * @param path - The API endpoint path
   * @param body - Request body data
   * @param options - Additional request options
   * @param urlParams - URL query parameters
   * @param returnResponse - Whether to return raw Response object
   * @returns Promise resolving to API response or raw Response
   */
  protected async request<T = object>(
    method: HTTPMethod,
    path: string,
    body: any = {},
    options: RequestOptions = {},
    urlParams: URLParams = {},
    returnResponse: boolean = false
  ): Promise<APIResponse<T> | Response | null> {
    this.lastRequest = null;

    try {
      const url = this.buildURL(path, urlParams);
      const requestOptions = this.parseOptions(options);

      requestOptions.method = method;

      // Define Content-Type if not specified for JSON body
      if (requestOptions.headers instanceof Headers && ! requestOptions.headers.has('Content-Type') && ! (body instanceof FormData)) {
        requestOptions.headers.append('Content-Type', 'application/json');
      }

      // Process the body
      if (body) {
        requestOptions.body = this.prepareBody(body);
      }

      // Log
      this.log(`(${method}) ${url}`);

      this.lastRequest = await fetch(url, requestOptions);

      if (returnResponse) {
        return this.lastRequest;
      }

      // Process the response
      if (! this.lastRequest.body) {
        return {
          success: true,
          data: {},
        } as T;
      }

      return await this.lastRequest.json() as T;
    } catch (error) {
      this.error(error as Error);

      if (returnResponse) {
        throw error;
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }
}
